/** Navigation receipts only: the server remains the authority for ownership and folder location. */
export const CONTINUE_DIAGRAM_PARAM = 'continueDiagram';
const PREFIX = 'creately:demo-continuation:';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const validId = (value: unknown): value is string => typeof value === 'string'
  && /^[a-zA-Z0-9_-]{1,128}$/.test(value) && !['new', 'test'].includes(value);
const hasExplicitIntent = (url: URL) => ['lt', 'invite', 'invitation', 'forwardAuth', 'path', 'newFolder', 'starter', 'templateId', 'seed', 'type', 'orgOptions']
  .some(key => url.searchParams.has(key));

export function demoDiagramFromUrl(url: URL): string | null {
  if (['lt', 'invite', 'invitation', 'forwardAuth'].some(key => url.searchParams.has(key))) return null;
  const id = url.pathname.match(/^\/f\/[^/]+\/d\/([^/]+)\/?$/)?.[1];
  return validId(id) ? id : null;
}

export function rememberDemoContinuation(userId: string, diagramId: string, storage?: Storage): void {
  if (!validId(diagramId)) return;
  try { (storage ?? window.localStorage).setItem(PREFIX + userId, JSON.stringify({ diagramId, expiresAt: Date.now() + MAX_AGE_MS })); } catch { /* URL handoff still works without storage. */ }
}

export function clearDemoContinuation(userId: string, storage?: Storage): void {
  try { (storage ?? window.localStorage).removeItem(PREFIX + userId); } catch { /* Storage may be disabled. */ }
}

export function pendingDemoContinuation(url: URL, userId: string | null, storage?: Storage): string | null {
  if (!userId || hasExplicitIntent(url) || !/^\/dashboard(?:\/[^/]+)?\/?$/.test(url.pathname)) return null;
  const explicit = url.searchParams.get(CONTINUE_DIAGRAM_PARAM);
  if (validId(explicit)) return explicit;
  if (!/^\/dashboard(?:\/(?:recent|myorg|personal))?\/?$/.test(url.pathname)) return null;
  try {
    const receipt = JSON.parse((storage ?? window.localStorage).getItem(PREFIX + userId) ?? 'null');
    return receipt?.expiresAt > Date.now() && validId(receipt.diagramId) ? receipt.diagramId : null;
  } catch { return null; }
}

export function demoCheckoutReturnUrl(origin: string, diagramId: string): string {
  const target = new URL('/dashboard/', origin);
  target.searchParams.set(CONTINUE_DIAGRAM_PARAM, diagramId);
  target.searchParams.set('dashboard_ui', 'new');
  return target.href;
}

export function continuationFolderId(diagram: { id?: string; folderId?: string; ownerUserId?: string; deletedAt?: unknown; archived?: boolean }, diagramId: string, userId: string): string {
  if (diagram.id !== diagramId || diagram.ownerUserId !== userId || !validId(diagram.folderId) || diagram.deletedAt || diagram.archived) {
    throw new Error('Your diagram is not ready to continue yet. Please try again.');
  }
  return diagram.folderId;
}

/** Require server-confirmed ownership before loading diagram content for a return intent. */
export async function resolveDemoContinuationFolder(fetcher: (url: string) => Promise<Response>, diagramId: string, userId: string): Promise<string> {
  const path = `/api/diagrams/${encodeURIComponent(diagramId)}`;
  const access = await fetcher(`${path}/access`);
  if (!access.ok || (await access.json()).role !== 'owner') throw new Error('Diagram ownership is not ready');
  const response = await fetcher(path);
  if (!response.ok) throw new Error('Diagram is not available');
  const body = await response.json();
  return continuationFolderId(body.diagram ?? {}, diagramId, userId);
}
